import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Trash2, Sparkles, Video } from "lucide-react";
import {
  adminListBlogVideos,
  adminUpsertBlogVideo,
  adminDeleteBlogVideo,
  adminGenerateVideoScript,
} from "@/lib/blog-videos.functions";
import { adminListPosts } from "@/lib/blog-admin.functions";

export const Route = createFileRoute("/admin/blog-videos")({
  component: BlogVideosPage,
  head: () => ({ meta: [{ title: "Blog videos · Admin" }, { name: "robots", content: "noindex" }] }),
});

function BlogVideosPage() {
  const list = useServerFn(adminListBlogVideos);
  const upsert = useServerFn(adminUpsertBlogVideo);
  const remove = useServerFn(adminDeleteBlogVideo);
  const genScript = useServerFn(adminGenerateVideoScript);
  const listPosts = useServerFn(adminListPosts);
  const qc = useQueryClient();

  const [postId, setPostId] = useState<string>("");
  const [script, setScript] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const q = useQuery({ queryKey: ["admin-blog-videos"], queryFn: () => list() });
  const p = useQuery({ queryKey: ["admin-posts-min"], queryFn: () => listPosts() });
  const rows = Array.isArray(q.data) ? (q.data as any[]) : [];
  const posts = Array.isArray(p.data) ? (p.data as any[]) : [];

  const draftScript = async () => {
    if (!postId) return toast.error("Pick a blog post first");
    setBusy(true);
    try {
      const r = (await genScript({ data: { post_id: postId } })) as { script: string };
      setScript(r.script);
      toast.success("Script drafted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Draft failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!postId || !script) return toast.error("Post + script required");
    setBusy(true);
    try {
      await upsert({
        data: {
          post_id: postId,
          provider: "heygen",
          aspect_ratio: "9:16",
          script,
          video_url: videoUrl || null,
          status: videoUrl ? "ready" : "draft",
        },
      });
      toast.success("Video saved");
      setScript("");
      setVideoUrl("");
      setPostId("");
      await qc.invalidateQueries({ queryKey: ["admin-blog-videos"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id: string) => {
    if (!confirm("Delete this video?")) return;
    try {
      await remove({ data: { id } });
      await qc.invalidateQueries({ queryKey: ["admin-blog-videos"] });
      toast.success("Deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div>
      <BlogSubNav />
      <h1 className="font-display text-2xl font-bold">Blog videos</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Vertical 9:16 explainer scripts per post. Paste a HeyGen (or Descript, Riverside) video URL once
        rendered — it will attach to the post for AI-search visibility and YouTube Shorts.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="font-semibold">Draft a new video</p>
          <div className="mt-4 space-y-3">
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={postId}
              onChange={(e) => setPostId(e.target.value)}
            >
              <option value="">Select a blog post…</option>
              {posts.map((post) => (
                <option key={post.id} value={post.id}>
                  {post.title} ({post.status})
                </option>
              ))}
            </select>
            <Button onClick={draftScript} disabled={busy || !postId} variant="outline" className="w-full">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
              Draft AI script
            </Button>
            <Textarea
              placeholder="Video script (45–60 seconds spoken)…"
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={9}
            />
            <Input
              placeholder="Optional: HeyGen video URL once rendered"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
            <Button onClick={save} disabled={busy || !script || !postId} className="w-full">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Video className="mr-1 h-4 w-4" />}
              Save video
            </Button>
          </div>
        </div>

        <div>
          <p className="mb-3 font-semibold">Existing videos ({rows.length})</p>
          <div className="space-y-3">
            {q.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No videos yet.</p>
            ) : (
              rows.map((v) => (
                <div key={v.id} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">
                        {v.blog_posts?.title ?? "(no post)"}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {v.aspect_ratio} · {v.provider} · {v.status}
                      </p>
                      {v.video_url && (
                        <a
                          href={v.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-block text-xs text-primary underline"
                        >
                          View video ↗
                        </a>
                      )}
                      {v.script && (
                        <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">{v.script}</p>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => del(v.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
